import type {
  AssessDocumentHealthOptions,
  DocumentHealth,
  DocumentHealthReport,
  DocumentHealthSignal,
  DocumentSignalResult,
} from "@/types/knowledge/knowledge-brain";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import { documentAgeDays } from "./topicClusters";

/**
 * Per-document health signals behind the review queue.
 *
 * THREE signals, two of which are live on this build:
 *
 *  - `stale`       — LIVE. Age of `updated_at` (falling back to `created_at`).
 *  - `unused`      — LIVE. Not attached to any Genie, or attached but never
 *                    activated into the Genie's knowledge, past a grace period.
 *  - `conflicting` — NOT AVAILABLE. Detecting two documents that disagree needs
 *                    to know which documents a Genie actually drew on when it
 *                    answered. That record does not exist yet; it is specified in
 *                    `docs/handoff/answer-to-document-logging.md`. The signal is
 *                    modelled and rendered as an explicit "not available yet"
 *                    state so it is never silently dropped and never faked.
 *
 * The same handoff would also let `stale` become usage-weighted — a stale
 * document the Genie leans on daily matters far more than one nobody hits.
 */

const DEFAULT_STALE_DAYS = 180;
const DEFAULT_UNUSED_GRACE_DAYS = 14;

/** `keep` decisions are persisted on document metadata via the existing update route. */
export const REVIEWED_AT_METADATA_KEY = "knowledge_reviewed_at";

export const readReviewedAt = (doc: DocumentWithMetadata): string | null => {
  const value = doc.metadata?.[REVIEWED_AT_METADATA_KEY];
  return typeof value === "string" && value ? value : null;
};

const formatAge = (days: number): string => {
  if (days >= 730) return `${Math.floor(days / 365)} years old`;
  if (days >= 365) return "over a year old";
  const months = Math.floor(days / 30);
  if (months >= 2) return `${months} months old`;
  return `${Math.floor(days)} days old`;
};

const assessStale = (
  doc: DocumentWithMetadata,
  staleAfterDays: number,
  now: Date,
): DocumentSignalResult => {
  const age = documentAgeDays(doc, now);
  if (age === null) {
    return {
      signal: "stale",
      state: "clear",
      reason: "No last-updated date recorded, so age cannot be judged.",
    };
  }
  if (age > staleAfterDays) {
    return {
      signal: "stale",
      state: "flagged",
      reason: `Last updated ${formatAge(age)} — check it still reflects how you work today.`,
    };
  }
  return { signal: "stale", state: "clear", reason: `Updated ${formatAge(age)}.` };
};

const assessUnused = (
  doc: DocumentWithMetadata,
  graceDays: number,
  now: Date,
): DocumentSignalResult => {
  const attachedCount = doc.dependent_agents?.length ?? 0;
  const activated = Boolean(doc.el_doc_id) && doc.is_active !== false;
  const age = documentAgeDays(doc, now);
  const withinGrace = age !== null && age <= graceDays;

  if (attachedCount === 0) {
    if (withinGrace) {
      return {
        signal: "unused",
        state: "clear",
        reason: "Added recently — still waiting to be attached to a Genie.",
      };
    }
    return {
      signal: "unused",
      state: "flagged",
      reason: "Not attached to any Genie, so nothing can answer from it.",
    };
  }

  if (!activated) {
    if (withinGrace) {
      return {
        signal: "unused",
        state: "clear",
        reason: "Attached to a Genie and still being prepared.",
      };
    }
    return {
      signal: "unused",
      state: "flagged",
      reason: "Attached to a Genie but never activated, so it is not being used.",
    };
  }

  return {
    signal: "unused",
    state: "clear",
    reason: `Live on ${attachedCount} ${attachedCount === 1 ? "Genie" : "Genies"}.`,
  };
};

const CONFLICTING_UNAVAILABLE: DocumentSignalResult = {
  signal: "conflicting",
  state: "unavailable",
  reason:
    "Needs a record of which sources each answer came from. Not available yet — it is on the roadmap.",
};

export const assessDocumentHealth = (
  documents: DocumentWithMetadata[],
  options: AssessDocumentHealthOptions = {},
): DocumentHealthReport => {
  const {
    staleAfterDays = DEFAULT_STALE_DAYS,
    unusedAfterDays = DEFAULT_UNUSED_GRACE_DAYS,
    now = new Date(),
  } = options;

  const all: DocumentHealth[] = documents.map((document) => {
    const signals: Record<DocumentHealthSignal, DocumentSignalResult> = {
      stale: assessStale(document, staleAfterDays, now),
      unused: assessUnused(document, unusedAfterDays, now),
      conflicting: CONFLICTING_UNAVAILABLE,
    };

    const flagged = (Object.keys(signals) as DocumentHealthSignal[]).filter(
      (key) => signals[key].state === "flagged",
    );

    const reviewedAt = readReviewedAt(document);
    const reviewedAfterLastEdit =
      reviewedAt !== null &&
      (() => {
        const updated = document.updated_at ? new Date(document.updated_at).getTime() : 0;
        const reviewed = new Date(reviewedAt).getTime();
        return Number.isFinite(reviewed) && reviewed >= updated;
      })();

    // Unused outranks stale: a document nothing can reach is a bigger problem
    // than one that is merely old.
    const priority =
      (signals.unused.state === "flagged" ? 2 : 0) + (signals.stale.state === "flagged" ? 1 : 0);

    return {
      documentId: document.id,
      document,
      signals,
      flagged,
      needsReview: flagged.length > 0 && !reviewedAfterLastEdit,
      priority,
      reviewedAt,
    };
  });

  const queue = all
    .filter((h) => h.needsReview)
    .sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority;
      const aAt = a.document.updated_at ? new Date(a.document.updated_at).getTime() : 0;
      const bAt = b.document.updated_at ? new Date(b.document.updated_at).getTime() : 0;
      return aAt - bAt; // oldest first
    });

  const counts: Record<DocumentHealthSignal, number> = {
    stale: all.filter((h) => h.signals.stale.state === "flagged").length,
    unused: all.filter((h) => h.signals.unused.state === "flagged").length,
    conflicting: 0,
  };

  return {
    all,
    queue,
    counts,
    unavailableSignals: ["conflicting"],
  };
};

export const SIGNAL_LABELS: Record<DocumentHealthSignal, string> = {
  stale: "Stale",
  unused: "Unused",
  conflicting: "Conflicting",
};
